import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Quote, BookOpen, HandHeart, Users, HeartPulse, Sparkles,
  Cross, Target, Gem, Lightbulb, Globe, Rocket, Building2, Handshake, Radio, GraduationCap, Music2, ArrowRight, Download, ShieldCheck, TrendingUp,
} from "lucide-react";
import api from "@/lib/api";
import { API } from "@/lib/api";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Overline, GoldRule } from "@/components/Bits";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { PORTRAIT } from "@/lib/assets";

const ICONS = [BookOpen, HandHeart, Users, HeartPulse, Sparkles, Cross, Target, Gem, Lightbulb, Globe, Rocket, Building2, Handshake, Radio, GraduationCap, Music2];

function ChapterHeading({ n, overline, title, tagline }) {
  return (
    <Reveal className="mb-12">
      <div className="flex items-baseline gap-5">
        <span className="font-serif text-4xl md:text-5xl text-gold-500/50">{n}</span>
        <div>
          {overline && <Overline>{overline}</Overline>}
          <h2 className="mt-3 font-serif text-3xl md:text-5xl leading-[0.95] text-white">{title}</h2>
          {tagline && <p className="mt-3 font-accent italic text-lg md:text-xl text-gold-300">{tagline}</p>}
        </div>
      </div>
    </Reveal>
  );
}

function PullQuote({ children }) {
  return (
    <Reveal className="my-14">
      <div className="glass corner-frame p-8 md:p-12 text-center">
        <Quote size={30} className="text-gold-500 mx-auto mb-5" />
        <p className="font-accent italic text-2xl md:text-4xl leading-tight text-white">"{children}"</p>
      </div>
    </Reveal>
  );
}

function ItemGrid({ items, cols = 3, startIcon = 0 }) {
  return (
    <div className={`grid gap-px bg-gold-500/15 border border-gold-500/15 md:grid-cols-2 ${cols === 3 ? "lg:grid-cols-3" : ""}`}>
      {items.map((it, i) => {
        const Icon = ICONS[(startIcon + i) % ICONS.length];
        return (
          <Reveal key={it.title} delay={(i % 3) * 0.05}>
            <div className="h-full bg-ink p-8 hover:bg-ink-paper transition-colors duration-500">
              <Icon size={24} className="text-gold-500" strokeWidth={1.4} />
              <h3 className="mt-6 font-serif text-xl text-white">{it.title}</h3>
              <p className="mt-3 text-sm text-white/60 leading-relaxed">{it.desc}</p>
            </div>
          </Reveal>
        );
      })}
    </div>
  );
}

export default function Profile() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/profile").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <div className="min-h-screen" />;

  const wrap = "mx-auto max-w-[1400px] px-6 md:px-10";

  return (
    <div data-testid="profile-page">
      <PageHero
        overline="Executive Corporate Profile"
        title={<>Apostle <span className="font-accent text-gold-gradient italic">Blaise Judah</span></>}
        subtitle="Kingdom Leader · Pastor · Music Minister · Business Strategist · AI Educator · International Speaker"
      />
      <div className={`${wrap} pt-6 pb-2 flex flex-wrap items-center justify-between gap-4`}>
        <p className="text-xs uppercase tracking-[0.4em] text-gold-500">Faith · Leadership · Innovation · Impact</p>
        <a
          href={`${API}/profile/download`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="download-profile-btn"
          className="inline-flex items-center gap-2 border border-gold-500/40 px-5 py-2.5 text-xs uppercase tracking-[0.15em] text-white/85 hover:bg-gold-500 hover:text-ink transition-colors duration-300"
        >
          <Download size={14} /> Download Profile (PDF)
        </a>
      </div>

      {/* 02 Chairman's Message */}
      <section className={`${wrap} py-20`} data-testid="profile-chairman">
        <ChapterHeading n="02" overline="Chairman's Message" title="A word from the founder" />
        <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr] lg:items-start">
          <Reveal>
            <div className="corner-frame border border-gold-500/25 p-1.5 lg:sticky lg:top-28">
              <img src="/gallery/library.jpg" alt="Apostle Blaise Judah" className="aspect-[4/5] w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-accent italic text-2xl md:text-3xl text-gold-200">{d.chairman_greeting}</p>
            <div className="mt-6 space-y-4">
              {d.chairman_message.map((p, i) => (
                <p key={i} className={`text-white/72 leading-relaxed ${i === 0 ? "first-letter:font-serif first-letter:text-6xl first-letter:text-gold-400 first-letter:mr-3 first-letter:float-left first-letter:leading-[0.8]" : ""}`}>{p}</p>
              ))}
            </div>
            <p className="mt-6 text-white/60">{d.chairman_signoff}</p>
            <p className="mt-3 font-accent italic text-3xl text-white">Apostle Blaise Judah</p>
            <p className="text-xs uppercase tracking-[0.2em] text-gold-500 mt-1">Founder & Senior Pastor · House of Blessing Ministries</p>
          </Reveal>
        </div>
      </section>

      {/* 04 About */}
      <section className={`${wrap} py-16 border-t border-gold-500/15`} data-testid="profile-about">
        <ChapterHeading n="04" overline="About" title="About Apostle Blaise Judah" />
        <div className="grid gap-12 lg:grid-cols-[1fr_1.3fr] lg:items-center">
          <Reveal>
            <div className="corner-frame border border-gold-500/25 p-1.5">
              <img src={PORTRAIT} alt="Apostle Blaise Judah" className="aspect-[4/5] w-full object-cover object-[center_20%]" />
            </div>
            <div className="mt-8 border-l-2 border-gold-500 pl-6">
              <p className="font-accent italic text-xl text-white/90 leading-snug">"{d.leadership_quote}"</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-5">
              {d.story.map((p, i) => <p key={i} className="text-white/70 leading-relaxed">{p}</p>)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* 05 Brand Positioning */}
      <section className={`${wrap} py-16 border-t border-gold-500/15`} data-testid="profile-brand">
        <ChapterHeading n="05" overline="Brand Positioning" title="Brand & values" />
        <div className="grid gap-6 lg:grid-cols-2">
          {[["Brand Essence", d.brand_essence], ["Brand Promise", d.brand_promise], ["Vision Statement", d.vision], ["Mission Statement", d.mission]].map(([label, body], i) => (
            <Reveal key={label} delay={(i % 2) * 0.06}>
              <div className="h-full glass corner-frame p-8">
                <Overline>{label}</Overline>
                <p className="mt-5 text-white/75 leading-relaxed">{body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-14 mb-6"><Overline>Core Values</Overline></Reveal>
        <ItemGrid items={d.core_values} startIcon={5} />
      </section>

      <EditorialMarquee text="FAITH · LEADERSHIP · INNOVATION · IMPACT" />

      {/* 06 Milestones */}
      <section className={`${wrap} py-20`} data-testid="profile-milestones">
        <ChapterHeading n="06" overline="Our Story" title="The journey of grace and purpose" />
        <div className="border-t border-gold-500/20">
          {d.milestones.map((m, i) => (
            <Reveal key={m.year} delay={i * 0.04}>
              <div className="grid gap-6 border-b border-gold-500/20 py-7 md:grid-cols-[160px_1fr] md:gap-14 md:items-baseline hover:bg-ink-paper/30 transition-colors duration-500">
                <span className="font-serif text-4xl md:text-5xl text-gold-gradient leading-none">{m.year}</span>
                <div><h3 className="font-serif text-2xl text-white">{m.title}</h3><p className="mt-2 text-white/65 leading-relaxed">{m.desc}</p></div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 08 House of Blessing Ministries */}
      <section className={`${wrap} py-16 border-t border-gold-500/15`} data-testid="profile-hob">
        <ChapterHeading n="08" overline="The Ministry" title="House of Blessing Ministries" tagline={d.hob.tagline} />
        <Reveal className="mb-10"><p className="max-w-3xl text-white/70 leading-relaxed text-lg">{d.hob.intro}</p></Reveal>
        <ItemGrid items={d.hob.focus_areas} />
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {d.hob.impact.map((im, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="border border-gold-500/20 bg-ink-royal/20 p-7 h-full">
                <Globe size={22} className="text-gold-500" strokeWidth={1.4} />
                <p className="mt-4 text-sm text-white/75 leading-relaxed">{im}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 09 Ministry Philosophy */}
      <section className={`${wrap} py-16 border-t border-gold-500/15`} data-testid="profile-philosophy">
        <ChapterHeading n="09" overline="Ministry Philosophy" title="Leading with love" tagline={d.ministry_philosophy.tagline} />
        <Reveal className="mb-10"><p className="max-w-3xl text-white/70 leading-relaxed text-lg">{d.ministry_philosophy.intro}</p></Reveal>
        <ItemGrid items={d.ministry_philosophy.principles} startIcon={5} />
        <PullQuote>{d.ministry_philosophy.quote}</PullQuote>
      </section>

      {/* 10 Worship, Prayer & Discipleship */}
      <section className={`${wrap} py-16 border-t border-gold-500/15`} data-testid="profile-worship">
        <ChapterHeading n="10" overline="Worship · Prayer · Discipleship" title="Encountering God" tagline={d.worship_discipleship.tagline} />
        <Reveal className="mb-10"><p className="max-w-3xl text-white/70 leading-relaxed text-lg">{d.worship_discipleship.intro}</p></Reveal>
        <div className="grid gap-6 md:grid-cols-3">
          {d.worship_discipleship.items.map((it, i) => {
            const Icon = [Music2, HandHeart, BookOpen][i] || Sparkles;
            return (
              <Reveal key={it.title} delay={i * 0.06}>
                <div className="h-full border border-gold-500/20 bg-ink-paper/40 p-8 text-center">
                  <Icon size={30} className="text-gold-500 mx-auto" strokeWidth={1.3} />
                  <h3 className="mt-6 font-serif text-2xl text-white">{it.title}</h3>
                  <p className="mt-3 text-sm text-white/60 leading-relaxed">{it.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          {[["Our Commitment", d.worship_discipleship.commitment], ["Our Goal", d.worship_discipleship.goal]].map(([l, b]) => (
            <Reveal key={l}><div className="border border-gold-500/20 bg-ink-royal/20 p-7"><Overline>{l}</Overline><p className="mt-4 text-white/75 leading-relaxed">{b}</p></div></Reveal>
          ))}
        </div>
        <PullQuote>{d.gospel_quote}</PullQuote>
      </section>

      {/* 11 Community Impact */}
      <section className={`${wrap} py-16 border-t border-gold-500/15`} data-testid="profile-community">
        <ChapterHeading n="11" overline="Community Impact" title="Serving people" tagline={d.community_impact.tagline} />
        <Reveal className="mb-10"><p className="max-w-3xl text-white/70 leading-relaxed text-lg">{d.community_impact.intro}</p></Reveal>
        <ItemGrid items={d.community_impact.items} />
        <PullQuote>{d.community_impact.quote}</PullQuote>
      </section>

      {/* 12 Global Outreach */}
      <section className={`${wrap} py-16 border-t border-gold-500/15`} data-testid="profile-global">
        <ChapterHeading n="12" overline="Global Outreach" title="Touching nations" tagline={d.global_outreach.tagline} />
        <Reveal className="mb-10"><p className="max-w-3xl text-white/70 leading-relaxed text-lg">{d.global_outreach.intro}</p></Reveal>
        <ItemGrid items={d.global_outreach.items} startIcon={9} />
        <PullQuote>{d.global_outreach.quote}</PullQuote>
      </section>

      {/* 13 Future Vision */}
      <section className={`${wrap} py-16 border-t border-gold-500/15`} data-testid="profile-future">
        <ChapterHeading n="13" overline="Future Ministry Vision" title="A future of impact" tagline={d.future_vision.tagline} />
        <Reveal className="mb-10"><p className="max-w-3xl text-white/70 leading-relaxed text-lg">{d.future_vision.intro}</p></Reveal>
        <ItemGrid items={d.future_vision.items} startIcon={10} />
        <PullQuote>{d.future_vision.quote}</PullQuote>
      </section>

      {/* 14 Business & Global Impact */}
      <section className={`${wrap} py-16 border-t border-gold-500/15`} data-testid="profile-business">
        <ChapterHeading n="14" overline="Business & Global Impact" title="Kingdom principles, global influence" tagline={d.business_impact.tagline} />
        <Reveal className="mb-10"><p className="max-w-3xl text-white/70 leading-relaxed text-lg">{d.business_impact.intro}</p></Reveal>
        <div className="grid gap-px bg-gold-500/15 border border-gold-500/15 sm:grid-cols-2 lg:grid-cols-4">
          {d.business_impact.pillars.map((p, i) => {
            const Icon = [ShieldCheck, Gem, HeartPulse, Globe][i] || Sparkles;
            return (
              <Reveal key={p} delay={(i % 4) * 0.05}>
                <div className="h-full bg-ink p-8 hover:bg-ink-paper transition-colors duration-500">
                  <Icon size={24} className="text-gold-500" strokeWidth={1.4} />
                  <h3 className="mt-6 font-serif text-lg text-white">{p}</h3>
                </div>
              </Reveal>
            );
          })}
        </div>
        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          <Reveal>
            <div className="h-full border border-gold-500/20 bg-ink-royal/20 p-7" data-testid="profile-commodity">
              <TrendingUp size={22} className="text-gold-500" />
              <h4 className="mt-4 font-serif text-xl text-white">Commodity Trading & International Business</h4>
              <p className="mt-1 font-accent italic text-gold-300">{d.business_impact.commodity.tagline}</p>
              <p className="mt-3 text-sm text-white/65 leading-relaxed">{d.business_impact.commodity.intro}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {d.business_impact.commodity.areas.map((a) => <span key={a} className="border border-gold-500/25 px-3 py-1 text-[11px] text-white/70">{a}</span>)}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.06}>
            <div className="h-full border border-gold-500/20 bg-ink-royal/20 p-7">
              <BookOpen size={22} className="text-gold-500" />
              <h4 className="mt-4 font-serif text-xl text-white">Publications & Books</h4>
              <p className="mt-1 font-accent italic text-gold-300">{d.business_impact.publications.tagline}</p>
              <p className="mt-3 text-sm text-white/65 leading-relaxed">{d.business_impact.publications.intro}</p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full border border-gold-500/20 bg-ink-royal/20 p-7">
              <Music2 size={22} className="text-gold-500" />
              <h4 className="mt-4 font-serif text-xl text-white">Music Ministry</h4>
              <p className="mt-1 font-accent italic text-gold-300">{d.business_impact.music.tagline}</p>
              <p className="mt-3 text-sm text-white/65 leading-relaxed">{d.business_impact.music.intro}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {d.business_impact.music.areas.map((a) => <span key={a} className="border border-gold-500/25 px-3 py-1 text-[11px] text-white/70">{a}</span>)}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 15 Vision 2035 */}
      <section className="relative border-t border-gold-500/15 bg-ink-royal/15 py-20" data-testid="profile-vision2035">
        <div className={wrap}>
          <ChapterHeading n="15" overline={d.vision_2035.title} title="Building a global Kingdom impact network" tagline={d.vision_2035.subtitle} />
          <Reveal className="mb-12"><p className="max-w-3xl text-white/70 leading-relaxed text-lg">{d.vision_2035.intro}</p></Reveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
            {d.vision_2035.goals.map((g, i) => (
              <Reveal key={i} delay={(i % 5) * 0.05}>
                <div className="h-full border border-gold-500/25 bg-ink p-6 text-center">
                  <p className="font-serif text-3xl text-gold-gradient leading-tight">{g.metric}</p>
                  <p className="mt-3 text-xs uppercase tracking-[0.15em] text-white/70 leading-relaxed">{g.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 16 Strategic Partners */}
      <section className={`${wrap} py-16 border-t border-gold-500/15`} data-testid="profile-partners">
        <ChapterHeading n="16" overline="Connect & Partner" title="Build the Kingdom with us" />
        <Reveal className="mb-10"><p className="max-w-3xl text-white/70 leading-relaxed text-lg">{d.partners.intro}</p></Reveal>

        {(d.partners.endorsements || []).length > 0 && (
          <div className="mb-12">
            <Reveal className="mb-6"><Overline>Partners & Endorsements</Overline></Reveal>
            <div className="grid gap-6 md:grid-cols-2">
              {d.partners.endorsements.map((e) => {
                const plate = (
                  <div className="shrink-0 rounded-md bg-white px-6 py-5 flex items-center justify-center w-full sm:w-56 transition-transform duration-300 hover:scale-[1.03]">
                    <img src={e.logo} alt={e.name} className="h-16 w-auto object-contain" />
                  </div>
                );
                return (
                  <Reveal key={e.name}>
                    <div className="flex flex-col sm:flex-row items-center gap-6 border border-gold-500/25 bg-ink-paper/40 p-7" data-testid="endorsement-aiaie">
                      {e.url ? (
                        <a href={e.url} target="_blank" rel="noopener noreferrer" data-testid="endorsement-link" aria-label={e.name}>{plate}</a>
                      ) : plate}
                      <div className="text-center sm:text-left">
                        <h4 className="font-serif text-xl text-white">{e.short}</h4>
                        <p className="text-xs uppercase tracking-[0.15em] text-gold-400 mt-1">{e.name}</p>
                        <p className="mt-3 text-sm text-white/65 leading-relaxed">{e.desc}</p>
                        {e.url && (
                          <a href={e.url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-gold-400 hover:text-gold-300 transition-colors">
                            Visit website <ArrowUpRight size={13} />
                          </a>
                        )}
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        )}

        <Reveal className="mb-6"><Overline>Our Network</Overline></Reveal>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {d.partners.list.map((p, i) => (
            <Reveal key={p.name} delay={(i % 4) * 0.05}>
              <div className="h-full border border-gold-500/20 bg-ink-paper/40 p-7 hover:border-gold-400/60 transition-colors duration-500">
                <Handshake size={22} className="text-gold-500" strokeWidth={1.4} />
                <h4 className="mt-5 font-serif text-lg text-white">{p.name}</h4>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Closing */}
      <section className="relative border-t border-gold-500/20 py-24 text-center" data-testid="profile-closing">
        <Reveal className="mx-auto max-w-3xl px-6">
          <p className="font-accent italic text-3xl md:text-5xl leading-tight text-white">"{d.signature_quote}"</p>
          <p className="mt-8 text-xs uppercase tracking-[0.3em] text-gold-500">Apostle Blaise Judah</p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-gold-500 px-8 py-4 text-sm font-semibold text-ink hover:bg-gold-400 active:scale-[0.98] transition-[background,transform] duration-300">
              Partner With Us <ArrowUpRight size={16} />
            </Link>
            <Link to="/about" className="inline-flex items-center gap-2 border border-gold-500/40 px-8 py-4 text-sm text-white/85 hover:border-gold-400 hover:text-gold-300 transition-colors duration-300">
              Back to About <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
